import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatMenuTrigger } from '@angular/material/menu';
import { TranslateService } from '@ngx-translate/core';

enum DateRange {
  TenMinutes = '10min',
  ThirtyMinutes = '30min',
  OneHour = '1hour',
  SixHours = '6hour',
  TwelveHours = '12hour',
  TwentyFourHours = '24hour',
  SevenDays = '7days',
  FourteenDays = '14days',
  ThirtyDays = '30days',
  SixtyDays = '60days',
  NinetyDays = '90days',
  SixMonths = '6months',
  OneYear = '1year'
}

@Component({
  selector: 'custom-date-picker',
  templateUrl: './custom-date.component.html',
  styleUrls: ['./custom-date.component.scss']
})
export class CustomDatePickerComponent implements OnInit {
  @Input() pickerType: 'date' | 'datetime' = 'date';
  @Input() dates: { from: Date | null, to: Date | null } | null = null;
  @Input() enableMaxToday: boolean = false;
  @Input() required: boolean = false; 
  @Output() dateChange: EventEmitter<{ from: Date | null; to: Date | null }> = new EventEmitter();
  @ViewChild('dateMenuTrigger') dateMenuTrigger: MatMenuTrigger;

  fromDate: Date | null = null;
  toDate: Date | null = null;
  showPanel: boolean = false;
  today: Date = new Date();

  dateRanges = DateRange;

  searchDateRange: string = '';

  displayDates: string = '';

  constructor(
    private _translate: TranslateService,
    private datePipe: DatePipe
  ) {
    this._translate.setDefaultLang(localStorage.getItem('language') || 'en');
  }

  ngOnInit() {
    if (this.dates) {
      this.fromDate = this.dates.from;
      this.toDate = this.dates.to;
      this.displayDates = this.getFieldDisplayLabel();
    }
  }

  applyDateFilter() {
    this.displayDates = this.getFieldDisplayLabel();

    this.dateMenuTrigger.closeMenu();

    this.dateChange.emit({ from: this.fromDate, to: this.toDate });
  }

  resetDateFilter() {
    this.fromDate = null;
    this.toDate = null;
    this.displayDates = '';
    // this.setRange(DateRange.TwentyFourHours);

    // this._widgetService.applyDateFilter$.next({ from: null, to: null });
  }

  setRange(range: DateRange) {
    const now = new Date();
    let from: Date;
    const hourInMs = 60 * 60 * 1000;
    const dayInMs = 24 * hourInMs;
    
    switch (range) {
      case DateRange.TenMinutes:
        from = new Date(now.getTime() - 10 * 60 * 1000);
        break;
      case DateRange.ThirtyMinutes:
        from = new Date(now.getTime() - 30 * 60 * 1000);
        break;
      case DateRange.OneHour:
        from = new Date(now.getTime() - hourInMs);
        break;
      case DateRange.SixHours:
        from = new Date(now.getTime() - 6 * hourInMs);
        break;
      case DateRange.TwelveHours:
        from = new Date(now.getTime() - 12 * hourInMs);
        break;
      case DateRange.TwentyFourHours:
        from = new Date(now.getTime() - dayInMs);
        break;
      case DateRange.SevenDays:
        from = new Date(now.getTime() - 7 * dayInMs);
        break;
      case DateRange.FourteenDays:
        from = new Date(now.getTime() - 14 * dayInMs);
        break;
      case DateRange.ThirtyDays:
        from = new Date(now.getTime() - 30 * dayInMs);
        break;
      case DateRange.SixtyDays:
        from = new Date(now.getTime() - 60 * dayInMs);
        break;
      case DateRange.NinetyDays:
        from = new Date(now.getTime() - 90 * dayInMs);
        break;
      case DateRange.SixMonths:
        from = new Date(now.getTime() - 182 * dayInMs);
        break;
      case DateRange.OneYear:
        from = new Date(now.getTime() - 365 * dayInMs);
        break;
      default:
        from = now;
    }

    this.fromDate = from;
    this.toDate = now;
  }

  get filteredRanges() {
    if (!this.searchDateRange) return Object.keys(this.dateRanges);
    return Object.keys(this.dateRanges).filter(key =>
      this.getRangeLabel(key).toLowerCase().includes(this.searchDateRange.toLowerCase())
    );
  }

  // Helper to get the label for each range key
  getRangeLabel(key: string): string {
    const labels: { [key: string]: string } = {
      TenMinutes: 'Ultimi 10 min',
      ThirtyMinutes: 'Ultimi 30 min',
      OneHour: 'Ultima 1 ora',
      SixHours: 'Ultime 6 ore',
      TwelveHours: 'Ultime 12 ore',
      TwentyFourHours: 'Ultime 24 ore',
      SevenDays: 'Ultimi 7 giorni',
      FourteenDays: 'Ultimi 14 giorni',
      ThirtyDays: 'Ultimi 30 giorni',
      SixtyDays: 'Ultimi 60 giorni',
      NinetyDays: 'Ultimi 90 giorni',
      SixMonths: 'Ultimi 6 mesi',
      OneYear: 'Ultimo anno'
    };
    return labels[key] || key;
  }

  getFieldDisplayLabel() {
    return this.fromDate && this.toDate 
      ? this.datePipe.transform(this.fromDate, this.getDatePipeFormat()) + ' - ' + this.datePipe.transform(this.toDate, this.getDatePipeFormat())
      : ''
  }

  getDatePipeFormat(): string {
    return this.pickerType === 'date' ? 'dd/MM/yyyy' : 'dd/MM/yyyy HH:mm';
  }
}
